import React, { useEffect } from "react";
import { Button } from "../../components/AdminSharedComponents";
import { AlertTriangle, FileText, X } from "lucide-react";

const FileSizeErrorModal = ({
  isOpen,
  onClose,
  maxSize,
  invalidFiles = [],
}) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                File Size Error
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Some files exceed the maximum allowed size
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800 mb-2">
                  Files that exceed the {maxSize}MB limit:
                </p>
                <div className="space-y-2">
                  {invalidFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white rounded border border-red-200 p-2"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileText className="h-4 w-4 text-red-600 flex-shrink-0" />
                        <span className="text-sm text-gray-800 truncate">
                          {file.name}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-red-600 ml-2">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 mb-1">
                  File Size Guidelines:
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Maximum file size: {maxSize}MB per file</li>
                  <li>
                    • Supported formats: Images, PDF, Office docs, Archives
                  </li>
                  <li>
                    • Please compress or resize large files before uploading
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200">
          <Button
            variant="primary"
            className="w-full flex items-center justify-center gap-2 py-3"
            onClick={onClose}
          >
            <FileText className="h-4 w-4" />I Understand - Try Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FileSizeErrorModal;
